import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { PageWrapper } from '../../styledComponents/wrappers';
import HoverableImageThumbnail from '../catalog/productImages/HoverableImageThumbnail';
import { FULFILLMENT_PAGE_SIZE } from '../../services/fulfillment/constants';

export const BIKE_QC_ROUTE = '/fulfillment/bike-qc';

export interface QcBikeItem {
  id: number;
  shopifyOrderId: number;
  shopifyOrderName: string;
  shopifyLineItemGid: string;
  sku: string | null;
  title: string | null;
  imageUrl: string | null;
  qcBinCode: string | null;
}

const QC_FALLBACK_BIN = 'QC-BUFFER';

const columns: GridColDef[] = [
  {
    headerName: '',
    field: 'imageUrl',
    width: 64,
    sortable: false,
    renderCell: (params: GridRenderCellParams<QcBikeItem>) => (
      <HoverableImageThumbnail
        src={params.row.imageUrl ?? undefined}
        identifier={params.row.sku ?? undefined}
      />
    ),
  },
  {
    headerName: 'Order #',
    field: 'shopifyOrderName',
    flex: 0.7,
  },
  {
    headerName: 'SKU',
    field: 'sku',
    flex: 0.8,
    valueFormatter: ({ value }) => value ?? '—',
  },
  {
    headerName: 'Title',
    field: 'title',
    flex: 1.5,
    valueFormatter: ({ value }) => value ?? '—',
  },
  {
    headerName: 'Location',
    field: 'qcBinCode',
    flex: 0.7,
    valueFormatter: ({ value }) => value ?? QC_FALLBACK_BIN,
  },
];

const BikeQcList: React.FC = () => {
  const history = useHistory();
  const [bikes, setBikes] = useState<QcBikeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: FULFILLMENT_PAGE_SIZE,
  });

  useEffect(() => {
    const fetchBikes = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/fulfillment/qc/bikes`);
        setBikes((response as AxiosResponse<{ items: QcBikeItem[] }>).data.items);
      } catch {
        setError('Failed to load QC queue.');
      } finally {
        setLoading(false);
      }
    };
    fetchBikes();
  }, []);

  const handleRowClick = (params: GridRowParams) => {
    history.push(`/fulfillment/bike-qc/${(params.row as QcBikeItem).id}`);
  };

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Bike QC
      </Typography>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={bikes}
          columns={columns}
          getRowId={row => row.id}
          onRowClick={handleRowClick}
          autoHeight
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          pageSizeOptions={[FULFILLMENT_PAGE_SIZE, FULFILLMENT_PAGE_SIZE * 2]}
          initialState={{
            sorting: { sortModel: [{ field: 'shopifyOrderName', sort: 'asc' }] },
            pagination: { paginationModel: { pageSize: FULFILLMENT_PAGE_SIZE } },
          }}
          sx={{ cursor: 'pointer' }}
          rowHeight={56}
        />
      )}
    </PageWrapper>
  );
};

export default BikeQcList;
